/**
 * Self-evaluation quizzes for the mobile teaching guide.
 * Currently only `douleur_thoracique` is populated (pilot for Collège Montmorency).
 * Schema:
 *   { title, description, questions: [{ q, options:[...], correct:idx, explanation? }] }
 */
import { QUIZZES_A } from './selfEvalQuizzesA';
import { QUIZZES_B } from './selfEvalQuizzesB';
import { QUIZZES_C } from './selfEvalQuizzesC';

export const SELF_EVAL_QUIZZES = {
  ...QUIZZES_A,
  ...QUIZZES_B,
  ...QUIZZES_C,

  douleur_thoracique: {
    title: 'Auto-évaluation — Douleur Thoracique',
    description: '10 questions pour valider votre compréhension du protocole MPDS. Seuil de réussite : 70 %.',
    questions: [
      {
        q: 'Quels sont les 5 champs à capter en PRIORITÉ avant tout questionnement clinique ?',
        options: [
          'Adresse, rappel, nom, âge, sexe',
          'Symptômes, médicaments, antécédents, âge, sexe',
          'Adresse, douleur, respiration, sueurs, allergies',
          'Nom, adresse, médecin, hôpital, ambulance',
        ],
        correct: 0,
        explanation: 'Sans l’adresse exacte + un numéro de rappel, l’ambulance ne part pas. Le reste du questionnement est secondaire.',
      },
      {
        q: 'Un patient gémit et fait des bruits respiratoires irréguliers très espacés. Que faut-il en conclure ?',
        options: [
          'Il respire normalement, on peut continuer le questionnement',
          'Il fait des râles agoniques (gasping) — ce N’EST PAS de la respiration efficace',
          'Il dort profondément',
          'Il fait une crise d’asthme',
        ],
        correct: 1,
        explanation: 'Les râles agoniques sont un signe d’arrêt cardiaque débutant. Ne JAMAIS les confondre avec une respiration normale.',
      },
      {
        q: 'Laquelle de ces descriptions est la PLUS évocatrice d’une douleur cardiaque ?',
        options: [
          '« Ça pique comme une aiguille »',
          '« Ça augmente quand je respire profondément »',
          '« C’est comme un étau, un serrement, un poids »',
          '« Ça brûle quand je touche la peau »',
        ],
        correct: 2,
        explanation: 'Les mots-clés cardiaques classiques sont : serrement, écrasement, poids, étau. La douleur qui change avec la respiration est plutôt pleurale.',
      },
      {
        q: 'Vers où une douleur cardiaque irradie-t-elle TYPIQUEMENT ?',
        options: [
          'Bras droit uniquement',
          'Bras gauche, mâchoire et/ou dos',
          'Pieds et chevilles',
          'Seulement le sternum, sans irradiation',
        ],
        correct: 1,
        explanation: 'L’irradiation au bras gauche, à la mâchoire ou au dos est un drapeau rouge cardiaque MAJEUR. Chez la femme et le diabétique, attention aux présentations atypiques.',
      },
      {
        q: 'Quelle est la triade classique du syndrome coronarien aigu ?',
        options: [
          'Fièvre, toux, frissons',
          'Diaphorèse (sueurs froides), dyspnée, nausée',
          'Maux de tête, vertige, vision trouble',
          'Diarrhée, vomissements, douleur abdominale',
        ],
        correct: 1,
        explanation: 'Sueurs froides + essoufflement au repos + nausée = signature très évocatrice d’infarctus en cours.',
      },
      {
        q: 'Quelle phrase est À ÉVITER lorsqu’on parle à un citoyen en panique ?',
        options: [
          '« Je suis avec vous, on avance ensemble »',
          '« Vous avez bien fait d’appeler »',
          '« Calmez-vous »',
          '« Pouvez-vous me dire ce que vous ressentez ? »',
        ],
        correct: 2,
        explanation: '« Calmez-vous » est invalidant et contre-productif. Préférez une présence vocale calme et structurée.',
      },
      {
        q: 'Vous pouvez autoriser l’aspirine pré-arrivée si le patient... (sélectionnez la condition complète)',
        options: [
          'A pris du Viagra dans les 24 dernières heures',
          'A plus de 16 ans, est conscient, sans allergie, sans saignement actif',
          'Est diabétique et a moins de 12 ans',
          'A déjà pris de la nitroglycérine ce jour',
        ],
        correct: 1,
        explanation: 'Aspirine 162-325 mg À CROQUER réduit la mortalité de 23 %. Conditions : > 16 ans, conscient, pas d’allergie, pas de saignement actif.',
      },
      {
        q: 'Quelle position pré-arrivée recommander pour un patient en douleur thoracique ?',
        options: [
          'Allongé à plat sur le dos',
          'Allongé sur le ventre',
          'Semi-assis (jamais à plat — augmente la précharge cardiaque)',
          'Debout en marchant pour activer la circulation',
        ],
        correct: 2,
        explanation: 'La position semi-assise réduit la précharge cardiaque et améliore le confort respiratoire.',
      },
      {
        q: 'Une femme de 65 ans appelle pour « fatigue intense + nausée ». Pourquoi faut-il penser à un infarctus ?',
        options: [
          'Parce que toutes les femmes simulent leurs symptômes',
          'Parce que les femmes présentent souvent des symptômes ATYPIQUES (fatigue, nausée, dyspnée) au lieu de la douleur thoracique classique',
          'Parce que c’est forcément une indigestion',
          'Parce que la fatigue est toujours cardiaque après 60 ans',
        ],
        correct: 1,
        explanation: 'Les femmes et les diabétiques font fréquemment des présentations atypiques. NE PAS étiqueter « anxiété » sans vérifier les facteurs de risque cardiaques.',
      },
      {
        q: 'Quel est le rôle FONDAMENTAL du répartiteur dans le MPDS ?',
        options: [
          'Diagnostiquer la maladie du patient',
          'Prescrire un traitement médicamenteux',
          'Attribuer une priorité d’intervention en < 60 secondes après les infos critiques',
          'Décider à quel hôpital transporter le patient',
        ],
        correct: 2,
        explanation: 'Le MPDS ne diagnostique pas — il TRIE et ATTRIBUE la priorité ambulancière rapidement, en moins de 60 secondes après les infos critiques.',
      },
    ],
  },

  morsures: {
    title: 'Auto-évaluation — Morsures et Piqûres',
    description: '10 questions pour valider votre compréhension du protocole MPDS. Seuil de réussite : 70 %.',
    questions: [
      {
        q: 'Quelle est la TOUTE PREMIÈRE préoccupation du répartiteur lors d’un appel pour morsure animale ?',
        options: [
          'Identifier la race exacte de l’animal',
          'La sécurité de la scène — l’animal est-il contenu / maîtrisé ?',
          'Savoir si l’animal est vacciné',
          'Demander une photo de la blessure',
        ],
        correct: 1,
        explanation: 'Sécurité d’abord : tant que l’animal n’est pas contenu, le patient et les intervenants restent en danger. On sécurise avant tout le reste.',
      },
      {
        q: 'Parmi ces situations, laquelle est une PRIORITÉ 1 (urgence vitale) ?',
        options: [
          'Une petite morsure de chat au doigt, patient calme',
          'Une piqûre de moustique qui démange',
          'Un patient piqué par une guêpe dont la gorge serre et le visage enfle',
          'Une égratignure superficielle d’un chien connu',
        ],
        correct: 2,
        explanation: 'Gorge qui serre + œdème du visage = anaphylaxie. C’est une urgence absolue : l’état peut se détériorer en quelques minutes.',
      },
      {
        q: 'Quels sont les signes évocateurs d’une réaction anaphylactique ?',
        options: [
          'Rougeur locale isolée au point de piqûre',
          'Difficulté à respirer, œdème du visage/gorge, urticaire généralisé, étourdissements',
          'Légère douleur qui disparaît en une heure',
          'Une seule marque de dent visible',
        ],
        correct: 1,
        explanation: 'L’anaphylaxie est SYSTÉMIQUE : voies respiratoires (gorge, souffle), peau (urticaire diffus), circulation (étourdissement, malaise). Un signe local isolé ne suffit pas.',
      },
      {
        q: 'Un patient a un auto-injecteur d’adrénaline (EpiPen) et présente une anaphylaxie. Que faites-vous ?',
        options: [
          'Attendre l’arrivée des paramédics avant toute injection',
          'Encourager l’administration IMMÉDIATE de l’EpiPen',
          'Lui conseiller de prendre un antihistaminique et d’attendre',
          'Lui demander d’aller à l’hôpital en voiture',
        ],
        correct: 1,
        explanation: 'En anaphylaxie, l’adrénaline est le traitement de première ligne : on encourage l’auto-injection sans délai, puis surveillance continue jusqu’aux secours.',
      },
      {
        q: 'Pourquoi une morsure au VISAGE ou au COU est-elle considérée à haut risque ?',
        options: [
          'Parce que c’est plus douloureux',
          'À cause de la proximité des gros vaisseaux et des voies respiratoires',
          'Parce que ça laisse des cicatrices',
          'Parce que c’est toujours causé par un gros animal',
        ],
        correct: 1,
        explanation: 'Le visage et le cou contiennent des vaisseaux importants et les voies aériennes : un saignement ou un œdème y devient rapidement critique.',
      },
      {
        q: 'Une chauve-souris a été trouvée dans la chambre d’une personne endormie, sans morsure visible. Quel est le risque ?',
        options: [
          'Aucun risque s’il n’y a pas de marque',
          'Un risque de rage même sans morsure apparente',
          'Seulement un risque d’allergie',
          'Un risque uniquement si la chauve-souris est morte',
        ],
        correct: 1,
        explanation: 'Les morsures de chauve-souris peuvent être invisibles. Une exposition en dormant justifie une évaluation du risque rabique — à documenter et signaler.',
      },
      {
        q: 'Comment contrôler un saignement important causé par une morsure ?',
        options: [
          'Appliquer un garrot systématiquement',
          'Pression directe sur la plaie avec un linge propre',
          'Laver abondamment à l’eau très chaude',
          'Ne rien faire et attendre les secours',
        ],
        correct: 1,
        explanation: 'La pression directe avec un linge propre est la première mesure. On surélève le membre au besoin; le garrot reste exceptionnel.',
      },
      {
        q: 'Pour une PIQÛRE d’insecte, quel conseil utile pouvez-vous donner à l’appelant ?',
        options: [
          'Écraser l’insecte sur la peau',
          'Si possible, capturer l’insecte dans un contenant pour l’identifier',
          'Percer le point de piqûre avec une aiguille',
          'Appliquer de la boue sur la piqûre',
        ],
        correct: 1,
        explanation: 'Capturer l’insecte (sans risque) aide à l’identification et au traitement. Pour un dard d’abeille, on le retire en le grattant, sans le pincer.',
      },
      {
        q: 'Quelle information est ESSENTIELLE à documenter pour toute morsure animale ?',
        options: [
          'La couleur des yeux de l’animal',
          'Le type d’animal et, si connu, son statut vaccinal / de propriété',
          'Le nom du vétérinaire de l’animal',
          'Le poids exact de l’animal',
        ],
        correct: 1,
        explanation: 'Le type d’animal et le statut vaccinal orientent le risque infectieux (rage, tétanos) et le suivi de santé publique.',
      },
      {
        q: 'Un patient anaphylactique est allongé, l’EpiPen a été administré. Que faites-vous ensuite ?',
        options: [
          'Raccrocher puisque l’EpiPen a été donné',
          'Rester en ligne et surveiller en continu (la réaction peut reprendre)',
          'Lui conseiller de se lever et de marcher',
          'Lui donner à boire et à manger',
        ],
        correct: 1,
        explanation: 'On ne raccroche pas : l’anaphylaxie peut rebondir (réaction biphasique). Surveillance continue, patient allongé, prêt à guider une 2e injection si besoin.',
      },
    ],
  },

  overdose_empoisonnement: {
    title: 'Auto-évaluation — Overdose / Empoisonnement',
    description: '10 questions pour valider votre compréhension du protocole. Seuil de réussite : 70 %.',
    questions: [
      {
        q: 'Quelle ressource téléphonique spécialisée doit être impliquée pour toute ingestion de substance ?',
        options: [
          'Info-Santé 811',
          'Le centre antipoison du Québec',
          'La pharmacie de garde',
          'Le 411',
        ],
        correct: 1,
        explanation: 'Le centre antipoison (1-800-463-5060) est LA référence : gardez le contenant en main pour répondre à ses questions.',
      },
      {
        q: 'Pourquoi demande-t-on si l’ingestion est INTENTIONNELLE ou ACCIDENTELLE ?',
        options: [
          'Pour remplir les statistiques',
          'Ça change la sécurité des lieux, l’approche de l’appel et l’implication possible de la police',
          'Pour décider si on envoie une ambulance ou non',
          'Ça ne change rien à la prise d’appel',
        ],
        correct: 1,
        explanation: 'Un geste volontaire implique sécurité, surveillance de la personne et ressources en santé mentale ou policières au besoin.',
      },
      {
        q: 'L’appelant veut faire vomir la victime « pour sortir le poison ». Votre consigne ?',
        options: [
          'Bonne idée, avec de l’eau salée',
          'NON — on ne fait JAMAIS vomir : risque d’aspiration et de brûlure au retour de certaines substances',
          'Oui, mais seulement si la substance est liquide',
          'Oui, en penchant la tête vers l’arrière',
        ],
        correct: 1,
        explanation: 'Faire vomir peut aggraver (voies respiratoires, produits corrosifs). Rien par la bouche sans avis du centre antipoison.',
      },
      {
        q: 'Surdose d’opioïdes : la victime ne respire plus et une trousse de naloxone est disponible. Quelle est la priorité ?',
        options: [
          'La naloxone d’abord — c’est l’antidote',
          'La RCR d’abord : on ne retarde JAMAIS les compressions; la naloxone est donnée par une 2e personne si possible',
          'Attendre les ambulanciers sans rien faire',
          'Une douche froide pour la réveiller',
        ],
        correct: 1,
        explanation: 'La cause du décès est l’hypoxie : les compressions maintiennent l’oxygénation. La naloxone vient en parallèle, jamais à la place.',
      },
      {
        q: 'Quels signes évoquent une surdose d’opioïdes au téléphone ?',
        options: [
          'Agitation, cris, rougeurs au visage',
          'Respiration très lente ou ronflante, lèvres bleutées, personne inconsciente, pupilles très petites',
          'Fièvre et toux grasse',
          'Démangeaisons et éternuements',
        ],
        correct: 1,
        explanation: 'Bradypnée, cyanose et inconscience = dépression respiratoire : le tableau classique des opioïdes.',
      },
      {
        q: 'Une adolescente a avalé un flacon complet d’acétaminophène il y a 30 minutes. Elle dit « se sentir correcte ». Que retenez-vous ?',
        options: [
          'Aucun symptôme = aucun danger, on annule',
          'L’acétaminophène est trompeur : peu de symptômes au début, mais toxicité grave différée pour le foie — ambulance maintenue',
          'On rappelle demain pour vérifier',
          'Un grand verre de lait neutralise le médicament',
        ],
        correct: 1,
        explanation: 'C’est LE piège de l’acétaminophène : la fenêtre de traitement se joue avant l’apparition des symptômes.',
      },
      {
        q: 'Que fait-on du contenant de la substance ingérée ?',
        options: [
          'On le jette pour éviter une autre ingestion',
          'On le garde pour les ambulanciers : substance, concentration et quantité',
          'On le rince et on le recycle',
          'On le photographie puis on le jette',
        ],
        correct: 1,
        explanation: 'Le contenant répond aux questions vitales : quoi, combien, quelle concentration — pour le centre antipoison et l’hôpital.',
      },
      {
        q: 'Ingestion accidentelle : un bambin a bu quelques gorgées de savon à vaisselle, il pleure fort et respire bien. Votre lecture ?',
        options: [
          'Urgence vitale : ambulance prioritaire',
          'Produit peu toxique + enfant qui respire bien et pleure vigoureusement = priorité moindre, centre antipoison + consignes de rappel',
          'Le faire vomir immédiatement',
          'Lui donner du pain pour absorber le produit',
        ],
        correct: 1,
        explanation: 'Pleurer fort = bonnes voies respiratoires. On rince la bouche, centre antipoison, et on rappelle si vomissements, somnolence ou difficulté à avaler/respirer.',
      },
      {
        q: 'Il y a une seringue au sol près de la victime. Que dites-vous à l’appelant ?',
        options: [
          'La ramasser et la jeter à la poubelle',
          'Ne pas la toucher à mains nues et la signaler aux intervenants — sa sécurité d’abord',
          'La rincer à l’eau',
          'La remettre dans la poche de la victime',
        ],
        correct: 1,
        explanation: 'Sécurité de la scène : risque de piqûre accidentelle. On signale, on ne manipule pas.',
      },
      {
        q: 'Après une dose de naloxone, la victime se réveille et veut partir. Pourquoi maintenez-vous l’ambulance ?',
        options: [
          'Pour vérifier ses papiers',
          'La naloxone agit moins longtemps que plusieurs opioïdes : la dépression respiratoire peut REVENIR quand l’effet s’estompe',
          'Parce que la naloxone rend malade pendant 24 heures',
          'Aucune raison, elle peut partir',
        ],
        correct: 1,
        explanation: 'La re-narcotisation est le danger classique après la naloxone : surveillance et transport nécessaires.',
      },
    ],
  },

  diabete: {
    title: 'Auto-évaluation — Problème diabétique',
    description: '10 questions pour valider votre compréhension du protocole. Seuil de réussite : 70 %.',
    questions: [
      {
        q: 'Avec quoi l’HYPOGLYCÉMIE est-elle le plus souvent confondue au téléphone ?',
        options: [
          'Une grippe',
          'L’ébriété : confusion, agressivité, sueurs, démarche titubante',
          'Une réaction allergique',
          'Un coup de soleil',
        ],
        correct: 1,
        explanation: 'L’hypoglycémie IMITE l’ébriété. Le réflexe : chercher un bracelet médical et questionner sur le diabète avant de conclure.',
      },
      {
        q: 'Quels sont les signes classiques d’HYPERGLYCÉMIE enseignés ?',
        options: [
          'Haleine fruitée, grande soif, urines fréquentes, grande fatigue',
          'Toux, fièvre, frissons',
          'Douleur thoracique et sueurs froides',
          'Vision double et perte d’équilibre',
        ],
        correct: 0,
        explanation: 'L’haleine fruitée est l’indice signature de l’hyperglycémie (acidocétose) — on la demande !',
      },
      {
        q: 'Un diabétique confus est encore CAPABLE D’AVALER de façon sécuritaire. Votre consigne ?',
        options: [
          'Rien par la bouche en aucun cas',
          'Du sucre rapide MAINTENANT (jus, bonbons, sachets de sucre) et surveiller son état de conscience',
          'Son insuline immédiatement',
          'Le faire marcher pour activer la circulation',
        ],
        correct: 1,
        explanation: 'La fenêtre d’action se ferme quand la conscience se détériore : sucre rapide pendant qu’il peut encore avaler.',
      },
      {
        q: 'Le même patient perd conscience. Vos consignes changent comment ?',
        options: [
          'On lui glisse du sucre dans la bouche quand même',
          'PLUS RIEN par la bouche : position latérale de sécurité et surveillance continue de la respiration',
          'On le rassoit sur une chaise',
          'On attend qu’il se réveille seul',
        ],
        correct: 1,
        explanation: 'État de conscience altéré = risque d’étouffement : jamais rien par la bouche. PLS + surveillance de la respiration.',
      },
      {
        q: 'L’appelant propose de donner l’INSULINE du patient confus « pour l’aider ». Votre réponse ?',
        options: [
          'Bonne idée, c’est son médicament',
          'NON — si c’est une hypoglycémie, l’insuline AGGRAVERAIT dangereusement la situation',
          'Oui, mais une demi-dose',
          'Oui, si le patient est d’accord',
        ],
        correct: 1,
        explanation: 'On ne peut pas mesurer la glycémie au téléphone : l’insuline sur une hypoglycémie peut être fatale.',
      },
      {
        q: 'Quelles sont les questions clés du protocole diabétique enseignées en classe ?',
        options: [
          'Étourdissements ? Sueurs ? Convulsions ? Grande soif ? Grande fatigue ?',
          'Toux ? Fièvre ? Mal de gorge ?',
          'Douleur au dos ? Anévrisme ?',
          'Voyage récent ? Vaccins à jour ?',
        ],
        correct: 0,
        explanation: 'Les 5 questions du questionnement diabétique — avec l’haleine fruitée comme indice d’hyperglycémie.',
      },
      {
        q: 'Un homme diabétique a pris son insuline ce matin mais a SAUTÉ son repas. Il tremble et sue. Que suspectez-vous ?',
        options: [
          'Une hyperglycémie',
          'Une hypoglycémie : insuline sans apport de sucre = chute du glucose',
          'Une réaction allergique à l’insuline',
          'Un coup de chaleur',
        ],
        correct: 1,
        explanation: 'Insuline + repas sauté = recette classique de l’hypoglycémie. Tremblements, sueurs, palpitations, concentration qui baisse.',
      },
      {
        q: 'Pourquoi garder la ligne ouverte avec un patient diabétique qui appelle lui-même ?',
        options: [
          'Pour lui tenir compagnie',
          'S’il cesse de répondre, c’est le signe que son état de conscience se détériore',
          'Pour vérifier son adresse toutes les minutes',
          'Ce n’est pas nécessaire',
        ],
        correct: 1,
        explanation: 'Sa voix est votre moniteur : hésitations, mots cherchés, silence = détérioration. On ne raccroche pas.',
      },
      {
        q: 'Un diabétique inconscient fait des convulsions. Quel lien enseigné en classe ?',
        options: [
          'Aucun lien entre diabète et convulsions',
          'L’hypoglycémie sévère peut provoquer des convulsions — c’est une question du protocole (nature 12 et 13)',
          'Les convulsions guérissent l’hypoglycémie',
          'C’est forcément de l’épilepsie',
        ],
        correct: 1,
        explanation: '« Faites-vous du diabète ? » fait partie du questionnement des convulsions : le cerveau privé de sucre peut convulser.',
      },
      {
        q: 'Le patient reprend ses esprits après le jus et refuse l’ambulance. Votre approche de répartiteur ?',
        options: [
          'Annuler sans discussion',
          'Expliquer le risque de rechute, recommander le transport ou au minimum des consignes de rappel immédiat et la présence d’un proche',
          'Le menacer d’appeler la police',
          'Lui dire de reprendre de l’insuline',
        ],
        correct: 1,
        explanation: 'L’hypoglycémie peut rechuter (surtout avec certains médicaments à longue action). On documente, on conseille, on sécurise.',
      },
    ],
  },

};
